import { Icon } from "common/Icon";
import { Checkbox } from "common/styleguide/Checkbox";
import { CTA } from "common/styleguide/CTA";
import { appCodeSelectors } from "data/resources/appCodes";
import { experienceActions, experienceSelectors } from "data/resources/experiences";
import React, { Fragment } from "react";
import Helmet from "react-helmet";
import { connect } from "react-redux";
import { Link, withRouter } from "react-router-dom";
import { getExperienceId } from "../../ExperiencesApp/ExperiencesApp";
import { DEFAULT_CONFIG, LoadableExperienceShow } from "../ExperienceShow";
import styles from "./Config.scss";

class _Config extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { pendingConfig: null };
  }

  componentDidUpdate(prevProps) {
    const { experience } = this.props;
    if (experience && prevProps.experience !== experience) {
      this.setState({ pendingConfig: null });
    }
  }

  onConfigChange = (pendingConfig) => {
    this.setState({ pendingConfig });
    this.props.saveConfig(pendingConfig);
  };

  copyToClipboard = () => {
    const { experience, appCode } = this.props;
    const url = `https://ridewithgps.com/experiences/${experience.id}?app_code=${appCode.code}`;

    // not bothering with the async clipboard api because the below works in any browser supporting that newer (lesser supported) api
    // see: https://stackoverflow.com/a/33928558
    if (window.clipboardData && window.clipboardData.setData) {
      // IE specific code path to prevent textarea being shown while dialog is visible.
      return window.clipboardData.setData("Text", url);
    } else if (document.queryCommandSupported && document.queryCommandSupported("copy")) {
      const textarea = document.createElement("textarea");
      textarea.textContent = url;
      textarea.style.position = "fixed"; // Prevent scrolling to bottom of page in MS Edge.
      document.body.appendChild(textarea);
      textarea.select();
      try {
        return document.execCommand("copy"); // Security exception may be thrown by some browsers.
      } catch (ex) {
        console.warn("Copy to clipboard failed.", ex);
        return false;
      } finally {
        document.body.removeChild(textarea);
      }
    }
  };

  render() {
    const { experience, history } = this.props;

    return (
      <div className={styles.Config}>
        {experience && <Helmet title={experience.name} />}
        <div className={styles.side}>
          {experience && (
            <ConfigForm
              unlimited={experience.unlimited}
              experienceId={experience.id}
              config={this.state.pendingConfig || experience.viewConfig || DEFAULT_CONFIG}
              onConfigChange={this.onConfigChange}
              copyLink={this.copyToClipboard}
              onCancel={() => {
                history.push(`/experiences/${experience.id}/edit`);
              }}
            />
          )}
        </div>
        <div className={styles.main}>
          <LoadableExperienceShow limitedFromConfig={!experience?.unlimited} />
        </div>
      </div>
    );
  }
}

class ConfigForm extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = { showAsCopied: false };
  }

  onCopy = () => {
    this.props.copyLink();
    this.setState({ showAsCopied: true });
    setTimeout(() => {
      this.setState({ showAsCopied: false });
    }, 5000);
  };

  render() {
    const { showAsCopied } = this.state;
    const { onCancel, config, onConfigChange, unlimited, experienceId } = this.props;

    const ConfigCheckbox = ({ name, label }) => (
      <Checkbox
        checked={!!config[name]}
        label={label}
        horizontal
        onChange={(e) => {
          onConfigChange({ ...config, [name]: e.currentTarget.checked });
        }}
      />
    );

    return (
      <form>
        <h2>Customize Your Experience Share Page</h2>
        <label>Overview</label>
        <ConfigCheckbox name="showDate" label="Show date" />
        <ConfigCheckbox name="showLocation" label="Show location" />
        <ConfigCheckbox name="showDescription" label="Show description" />
        <label>Routes</label>
        <ConfigCheckbox name="allowDownload" label="Allow guests to download routes" />
        {unlimited ? (
          <>
            <CTA onClick={this.onCopy} secondary>
              {showAsCopied && <Fragment>Copied!</Fragment>}
              {!showAsCopied && (
                <Fragment>
                  <Icon i="md-link" style={{ marginRight: "10px" }} />
                  Copy Link
                </Fragment>
              )}
            </CTA>
            <CTA link onClick={onCancel}>
              Cancel
            </CTA>
          </>
        ) : (
          <div className={styles.limitedCodesLink}>
            Each access code has a unique link to this page that includes the code in the instructions.
            <br />
            <Link to={`/experiences/${experienceId}/shortcut`}>View access codes</Link>
          </div>
        )}
      </form>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const id = getExperienceId(ownProps);
  return {
    experience: experienceSelectors.find(state, id),
    appCode: appCodeSelectors.allByExperienceId(state, id).first(),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  const id = getExperienceId(ownProps);
  return {
    saveConfig: (config) => dispatch(experienceActions.update(id, { experience: { view_config: config } })),
  };
};

export const Config = withRouter(connect(mapStateToProps, mapDispatchToProps)(_Config));
